import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Mail, Phone, Clock, Send, Loader2 } from "lucide-react";
import { Container, Eyebrow, buttonClass } from "../components/site";
import { Reveal } from "../components/motion";
import { useToast } from "../hooks/use-toast";
import { useI18n } from "../i18n";
import axios from "axios";
import getApiBaseUrl from "../utils/apiBaseUrl";

const BACKEND_URL = getApiBaseUrl();

const Contact = () => {
  const { toast } = useToast();
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/contact`, formData);

      toast({
        title: t("contact.okTitle"),
        description: t("contact.okBody"),
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      toast({
        title: t("contact.errTitle"),
        description: t("contact.errBody"),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Mail, title: t("contact.emailLabel"), value: "founder@optivratech.com", link: "mailto:founder@optivratech.com" },
    { icon: Phone, title: t("contact.phoneLabel"), value: "+91 86198 38718", link: "tel:+918619838718", ltr: true },
    { icon: Clock, title: t("contact.hoursLabel"), value: t("contact.hours"), link: null },
  ];

  return (
    <>
      <Header />

      <section className="relative overflow-hidden pt-36 pb-24 md:pt-44">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="float absolute -top-24 end-[-8%] h-96 w-96 rounded-full bg-brand/10 blur-3xl" />
        </div>
        <Container className="relative grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="rise">
              <Eyebrow>{t("contact.eyebrow")}</Eyebrow>
            </div>
            <h1 className="rise mt-5 font-display text-6xl leading-[1] text-ink md:text-7xl" style={{ "--delay": "80ms" }}>
              {t("contact.title")}
            </h1>
            <p className="rise mt-6 max-w-sm text-lg leading-relaxed text-ink-2" style={{ "--delay": "180ms" }}>
              {t("contact.lede")}
            </p>

            <ul className="mt-10 space-y-3">
              {contactInfo.map((info, i) => {
                const Icon = info.icon;
                const body = (
                  <>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-soft text-brand transition-transform duration-300 group-hover:scale-110">
                      <Icon size={18} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs text-ink-3">{info.title}</span>
                      <span className="block truncate text-[0.9375rem] font-medium text-ink" dir={info.ltr ? "ltr" : undefined}>
                        {info.value}
                      </span>
                    </span>
                  </>
                );
                return (
                  <Reveal as="li" key={info.title} delay={250 + i * 90}>
                    {info.link ? (
                      <a href={info.link} className="card-lift group flex items-center gap-4 rounded-2xl border border-line bg-surface p-4 hover:border-brand/40">
                        {body}
                      </a>
                    ) : (
                      <div className="group flex items-center gap-4 rounded-2xl border border-line bg-surface p-4">{body}</div>
                    )}
                  </Reveal>
                );
              })}
            </ul>
          </div>

          <Reveal className="lg:col-span-7" delay={150}>
            <form
              onSubmit={handleSubmit}
              className="space-y-5 rounded-[2rem] border border-line bg-surface p-6 shadow-xl shadow-black/[0.04] md:p-10"
            >
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="name" className="field-label">{t("contact.name")}</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required autoComplete="name" className="field" />
                </div>
                <div>
                  <label htmlFor="email" className="field-label">{t("contact.email")}</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required autoComplete="email" className="field" placeholder="you@company.com" dir="ltr" />
                </div>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="phone" className="field-label">
                    {t("contact.phone")} <span className="font-normal text-ink-3">({t("common.optional")})</span>
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} autoComplete="tel" className="field" placeholder="+91" dir="ltr" />
                </div>
                <div>
                  <label htmlFor="subject" className="field-label">{t("contact.subject")}</label>
                  <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} required className="field" placeholder={t("contact.subjectPh")} />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="field-label">{t("contact.message")}</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="field resize-y"
                  placeholder={t("contact.messagePh")}
                ></textarea>
              </div>

              <div className="flex flex-col-reverse gap-4 pt-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-ink-3">{t("contact.privacy")}</p>
                <button type="submit" disabled={isSubmitting} className={buttonClass("primary", "lg")}>
                  {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} className="rtl:-scale-x-100" />}
                  {isSubmitting ? t("contact.sending") : t("contact.send")}
                </button>
              </div>
            </form>
          </Reveal>
        </Container>
      </section>

      <Footer />
    </>
  );
};

export default Contact;
